import {ReactNode, useState} from "react";

// Значение «Без эффекта» в эффектах диммера (как на сервере, UserActionCatalog.EffectName)
const NO_EFFECT = "Без эффекта";

// Строка «канал 1 — …»: номер канала и значение
const CHANNEL_LINE = /^канал (\d+) — (.+)$/;

// Выключенное или пустое значение — скрывается галочкой «Только включённое»
export const isIdleLine = (line: string) => /— (Без эффекта|выключен)/i.test(line);

type EffectRow = { key: string; event: string; lines: string[]; extra?: ReactNode };

type EffectsTableProps = {
  rows: EffectRow[];
  onlyEnabled: boolean;
};

const shortChannel = (value: string) => (value === NO_EFFECT ? "—" : value);

// Сводка для свёрнутого вида: «3 события, все без эффектов» или «в 6 часов: к2 — Колесо»
const effectsSummary = (rows: { event: string; channels: Map<number, string> }[]) => {
  const active = rows
    .map((row) => {
      const list = Array.from(row.channels.entries()).filter(([, value]) => value !== NO_EFFECT);
      return list.length ? `${row.event}: ${list.map(([n, value]) => `к${n} — ${value}`).join(", ")}` : null;
    })
    .filter(Boolean);
  if (active.length === 0) return `${rows.length} ${eventsWord(rows.length)}, все без эффектов`;
  return active.join("; ");
};

const eventsWord = (count: number) => {
  const mod10 = count % 10;
  const mod100 = count % 100;
  if (mod10 === 1 && mod100 !== 11) return "событие";
  if (mod10 >= 2 && mod10 <= 4 && (mod100 < 12 || mod100 > 14)) return "события";
  return "событий";
};

// Эффекты диммера: одна таблица «событие × канал» вместо столбика «канал N — Без эффекта»; по умолчанию свёрнута
export const EffectsTable = ({rows, onlyEnabled}: EffectsTableProps) => {
  const [open, setOpen] = useState(false);
  const parsed = rows.map((row) => {
    const channels = new Map<number, string>();
    row.lines.forEach((line) => {
      const match = CHANNEL_LINE.exec(line);
      if (match) channels.set(Number(match[1]), match[2]);
    });
    return {...row, channels};
  });
  const numbers = Array.from(new Set(parsed.flatMap((row) => Array.from(row.channels.keys())))).sort((a, b) => a - b);
  const visibleRows = onlyEnabled
    ? parsed.filter((row) => Array.from(row.channels.values()).some((x) => x !== NO_EFFECT))
    : parsed;
  const visibleNumbers = onlyEnabled
    ? numbers.filter((n) => visibleRows.some((row) => (row.channels.get(n) ?? NO_EFFECT) !== NO_EFFECT))
    : numbers;
  const nothing = visibleRows.length === 0;

  return (
    <div className="settings-effects">
      <div className="settings-effects__head">
        <span className={`settings-effects__summary${nothing ? " settings-effects__summary--idle" : ""}`}>
          {effectsSummary(parsed)}
        </span>
        {!nothing && (
          <button type="button" className="station-settings__link settings-effects__toggle" onClick={() => setOpen((x) => !x)}>
            {open ? "▾ скрыть" : "▸ таблица"}
          </button>
        )}
      </div>
      {open && !nothing && (
        <div className="station-settings__table-wrap">
          <table className="station-settings__table settings-effects__table">
            <thead>
            <tr>
              <th>Событие</th>
              {visibleNumbers.map((n) => <th key={n}>к{n}</th>)}
              <th/>
            </tr>
            </thead>
            <tbody>
            {visibleRows.map((row) => (
              <tr key={row.key}>
                <td>{row.event}</td>
                {visibleNumbers.map((n) => {
                  const value = row.channels.get(n) ?? NO_EFFECT;
                  return (
                    <td key={n} className={value === NO_EFFECT ? "settings-effects__none" : "settings-effects__on"}>
                      {shortChannel(value)}
                    </td>
                  );
                })}
                <td>{row.extra}</td>
              </tr>
            ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

// Каналы диммера «канал 1 — включён, яркость 49%» — короткими метками
export const isChannelStates = (lines: string[]) =>
  lines.length > 0 && lines.every((line) => /^канал \d+ — (включён|выключен)/.test(line));

export const ChannelStates = ({lines, onlyEnabled}: { lines: string[]; onlyEnabled: boolean }) => (
  <div className="settings-channels">
    {lines.filter((line) => !onlyEnabled || !isIdleLine(line)).map((line) => {
      const match = /^канал (\d+) — (включён|выключен)(?:, (.*))?$/.exec(line);
      const on = match?.[2] === "включён";
      return (
        <span key={line} className={`settings-channels__item${on ? " settings-channels__item--on" : ""}`}>
          <i/>{match ? `${match[1]} — ${on ? "вкл" : "выкл"}${match[3] ? `, ${match[3].replace(/^яркость /, "")}` : ""}` : line}
        </span>
      );
    })}
  </div>
);

// Длинный список значений: первые строки сразу, остальное — по кнопке
const VISIBLE_LINES = 3;

export const LongLines = ({lines, onlyEnabled}: { lines: string[]; onlyEnabled: boolean }) => {
  const [open, setOpen] = useState(false);
  const shown = onlyEnabled ? lines.filter((line) => !isIdleLine(line)) : lines;
  if (shown.length === 0) return <span className="station-value__empty">ничего не включено</span>;
  const cut = shown.length > VISIBLE_LINES + 1 && !open;
  return (
    <>
      {(cut ? shown.slice(0, VISIBLE_LINES) : shown).map((line) => <div key={line}>{line}</div>)}
      {shown.length > VISIBLE_LINES + 1 && (
        <button type="button" className="station-settings__link settings-lines__more" onClick={() => setOpen((x) => !x)}>
          {open ? "▾ свернуть" : `▸ ещё ${shown.length - VISIBLE_LINES}`}
        </button>
      )}
    </>
  );
};
